import type {
  HealthSystemSummaryGridRow,
  HealthSystemSummaryRow,
} from '../interfaces/Dashboard';

/**
 * The API returns each summary row's name as "Health System Name (123)", where
 * 123 is the total request count. Splits into the clean name and the numeric
 * total - `null` when the name carries no parenthesised count at all, as
 * distinct from an explicit "(0)" - so callers can filter/sort on the total
 * without re-parsing the string.
 */
const splitHealthSystemName = (
  healthSystemName: string,
): { name: string; total: number | null } => {
  const [namePart, totalPart] = healthSystemName.split('(');
  if (totalPart === undefined) {
    return { name: namePart, total: null };
  }
  const total = Number(totalPart.replace(/[()]/g, ''));
  return { name: namePart, total: Number.isNaN(total) ? null : total };
};

export const toSummaryGridRows = (
  rows: HealthSystemSummaryRow[],
): HealthSystemSummaryGridRow[] =>
  rows
    .map((row) => {
      const { name, total } = splitHealthSystemName(row.healthSystemName);
      return {
        ...row,
        healthSystemName: name,
        totalRequests: total === null ? '' : String(total),
      };
    })
    .sort((a, b) => a.healthSystemName.localeCompare(b.healthSystemName));

/**
 * Drops rows whose embedded total is explicitly 0 - an EHR/facility/location
 * with no requests at all renders as an empty axis category or, in the
 * per-health-system grid, an entirely empty panel. Neither is worth showing.
 * Rows with no parenthesised count at all are left alone: that is a different
 * shape of row, not a reported zero.
 */
export const dropZeroTotalRows = <T extends { healthSystemName: string }>(
  rows: T[],
): T[] =>
  rows.filter((row) => splitHealthSystemName(row.healthSystemName).total !== 0);

export const sortByHealthSystemName = <T extends { healthSystemName: string }>(
  rows: T[],
): T[] =>
  [...rows].sort((a, b) =>
    a.healthSystemName.localeCompare(b.healthSystemName),
  );
